import { useGetParams } from './hooks/useGetParams';
import { useBroadcast } from './hooks/useBroadcast';
import { useNoiseLevel } from './hooks/useNoiseLevel';

import { Title } from './components/Title/Title';
import { NoizeGraf } from './components/NoizeGraf/NoizeGraf';
import { NoizeValue } from './components/NoizeValue/NoizeValue';

import posterDefault from './assets/images/bgPoster.png';
import bgDefaultVideo from './assets/video/bgVideo.webm';
import { useEffect, useMemo, useRef, useState } from 'react';
import useLocalStorage from './hooks/useLocalStorage';

import { motion } from 'motion/react';
import { WrapperSegment } from './components/WrapperSegment/WrapperSegment';

function App() {
  const { currentState, allStates } = useBroadcast();
  const [prevDelta, setPrevDelta] = useState(0);

  const [playerNumber, setPlayerNumber] = useLocalStorage('player', '88');

  const {
    bgVideo,
    bgPoster,
    calibrationOffset,
    deltaDuration,
    smoothingFactor,
    coeef,
  } = useGetParams();

  const { calibratedLevelDbSPL, currentMicrophone, addSmoothBoost } =
    useNoiseLevel({
      calibrationOffset,
      deltaDuration,
      smoothingFactor,
    });

  const lastExecutedRef = useRef(0);

  useEffect(() => {
    if (currentState !== 'S6') {
      return;
    }
    const now = Date.now();

    if (now - lastExecutedRef.current >= Number(deltaDuration)) {
      if (currentState === 'S6' && calibratedLevelDbSPL < 100) {
        let deltaFromName = 100 - calibratedLevelDbSPL;
        const delta = deltaFromName + prevDelta;
        setPrevDelta(delta);
        addSmoothBoost(deltaFromName);
      }
      lastExecutedRef.current = now;
    }
  }, [calibratedLevelDbSPL, currentState]);

  useEffect(() => {
    const deltaStates = ['S1', 'S2', 'S3', 'S4', 'S5'];

    if (!deltaStates.includes(currentState)) {
      return;
    }

    if (allStates && currentState) {
      let deltaFromName = 0;

      if (currentState === 'S1') {
        deltaFromName = 0;
      } else {
        deltaFromName = Number(allStates[currentState]?.value || 0) || 0;
      }

      if (deltaFromName !== prevDelta) {
        const delta = deltaFromName - prevDelta;

        setPrevDelta(deltaFromName);
        addSmoothBoost(delta);
      }
    }
  }, [currentState, allStates, addSmoothBoost, prevDelta]);

  useEffect(() => {
    const deltaStates = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'];
    if (!currentState || deltaStates.includes(currentState)) {
      return;
    }

    const nameState = allStates[currentState]?.value;

    if (nameState) {
      const nameStateWords = nameState.split(' ');
      const number = nameStateWords[nameStateWords.length - 1];

      setPlayerNumber(number);
    }
  }, [currentState, allStates]);

  useEffect(() => {
    if (currentMicrophone?.label) {
      console.log(currentMicrophone?.label);
    }
  }, [currentMicrophone]);

  const srcVideoPlayer = useMemo(() => {
    const protocol = window?.location.protocol || 'https:';
    return `${protocol}//stream-test.iactive.pro/sibir-hk/videos/noize/${
      playerNumber || '88'
    }.webm`;
  }, [playerNumber]);

  const fascia1 = Math.round(3306 / coeef);
  const fascia2 = Math.round(3306 / coeef);
  const fascia3 = Math.round(3306 / coeef);

  const fascia4 = Math.round(3640 / coeef);
  const fascia5 = Math.round(3640 / coeef);

  const fascia6 = Math.round(3248 / coeef);
  const fascia7 = Math.round(3248 / coeef);
  const fascia8 = Math.round(3248 / coeef);
  const fascia9 = Math.round(3248 / coeef);
  const fascia10 = Math.round(3248 / coeef);
  const fascia11 = Math.round(3248 / coeef);

  const fascia12 = Math.round(3640 / coeef);
  const fascia13 = Math.round(3640 / coeef);

  const fascia14 = Math.round(3306 / coeef);
  const fascia15 = Math.round(3306 / coeef);
  const fascia16 = Math.round(3306 / coeef);

  const segmentParameters = [
    { width: fascia1, left: '0', id: 0 },
    { width: fascia2, left: fascia1, id: 1 },
    { width: fascia3, left: fascia1 + fascia2, id: 2 },

    { width: fascia4, left: fascia1 + fascia2 + fascia3, id: 3 },
    { width: fascia5, left: fascia1 + fascia2 + fascia3 + fascia4, id: 4 },

    {
      width: fascia6,
      left: fascia1 + fascia2 + fascia3 + fascia4 + fascia5,
      id: 5,
    },
    {
      width: fascia7,
      left: fascia1 + fascia2 + fascia3 + fascia4 + fascia5 + fascia6,
      id: 6,
    },
    {
      width: fascia8,
      left: fascia1 + fascia2 + fascia3 + fascia4 + fascia5 + fascia6 + fascia7,
      id: 7,
    },
    {
      width: fascia9,
      left:
        fascia1 +
        fascia2 +
        fascia3 +
        fascia4 +
        fascia5 +
        fascia6 +
        fascia7 +
        fascia8,
      id: 8,
    },
    {
      width: fascia10,
      left:
        fascia1 +
        fascia2 +
        fascia3 +
        fascia4 +
        fascia5 +
        fascia6 +
        fascia7 +
        fascia8 +
        fascia9,
      id: 9,
    },
    {
      width: fascia11,
      left:
        fascia1 +
        fascia2 +
        fascia3 +
        fascia4 +
        fascia5 +
        fascia6 +
        fascia7 +
        fascia8 +
        fascia9 +
        fascia10,
      id: 10,
    },

    {
      width: fascia12,
      left:
        fascia1 +
        fascia2 +
        fascia3 +
        fascia4 +
        fascia5 +
        fascia6 +
        fascia7 +
        fascia8 +
        fascia9 +
        fascia10 +
        fascia11,
      id: 11,
    },
    {
      width: fascia13,
      left:
        fascia1 +
        fascia2 +
        fascia3 +
        fascia4 +
        fascia5 +
        fascia6 +
        fascia7 +
        fascia8 +
        fascia9 +
        fascia10 +
        fascia11 +
        fascia12,
      id: 12,
    },

    {
      width: fascia14,
      left:
        fascia1 +
        fascia2 +
        fascia3 +
        fascia4 +
        fascia5 +
        fascia6 +
        fascia7 +
        fascia8 +
        fascia9 +
        fascia10 +
        fascia11 +
        fascia12 +
        fascia13,
      id: 13,
    },
    {
      width: fascia15,
      left:
        fascia1 +
        fascia2 +
        fascia3 +
        fascia4 +
        fascia5 +
        fascia6 +
        fascia7 +
        fascia8 +
        fascia9 +
        fascia10 +
        fascia11 +
        fascia12 +
        fascia13 +
        fascia14,
      id: 14,
    },
    {
      width: fascia16,
      left:
        fascia1 +
        fascia2 +
        fascia3 +
        fascia4 +
        fascia5 +
        fascia6 +
        fascia7 +
        fascia8 +
        fascia9 +
        fascia10 +
        fascia11 +
        fascia12 +
        fascia13 +
        fascia14 +
        fascia15,
      id: 15,
    },
  ];

  return (
    <div className="app-wrapper">
      {segmentParameters.map((seg) => (
        <WrapperSegment
          coeef={coeef}
          width={seg.width}
          left={seg.left}
          key={seg.id}
          noiseLevel={calibratedLevelDbSPL}
        />
      ))}

      {/* 
      <div className="titre-wrapper">
        <Title />

        <div className="noize-wrapper">
          <NoizeGraf noiseLevel={calibratedLevelDbSPL} />
          <NoizeValue noiseLevel={calibratedLevelDbSPL} />
        </div>
      </div> */}

      {/* {bgVideo && (
        <video
          autoPlay="autoplay"
          muted="muted"
          loop
          id="bgVideo"
          poster={bgVideo === '1' ? posterDefault : bgPoster}
        >
          <source
            src={bgVideo === '1' ? bgDefaultVideo : bgVideo}
            type="video/webm"
          ></source>
        </video>
      )} */}

      {/* <motion.div
        key={srcVideoPlayer}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <video autoPlay="autoplay" muted="muted" loop id="bgVideo">
          <source src={srcVideoPlayer} type="video/webm"></source>
        </video>
      </motion.div> */}
    </div>
  );
}

export default App;
